import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { MaskedHeading } from "@/components/site/Motion";

const HERO_IMG =
  "https://images.pexels.com/photos/21856132/pexels-photo-21856132.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1600";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative min-h-screen flex items-center overflow-hidden">
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={HERO_IMG}
          alt="VIP1 Night Club atmosphere"
          className="w-full h-[120%] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/80 to-obsidian/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/40" />
      </motion.div>

      {/* ambient glow */}
      <div className="absolute -left-40 top-1/3 w-[600px] h-[600px] ambient-glow blur-3xl z-0 pointer-events-none" />

      <motion.div style={{ opacity }} className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex items-center gap-4 mb-8"
        >
          <span className="h-px w-12 bg-brand" />
          <span className="font-display text-xs md:text-sm tracking-[0.3em] uppercase text-white/80">
            {t.hero.overline}
          </span>
        </motion.div>

        <h1 className="font-heading font-600 leading-[0.92] text-white text-6xl sm:text-7xl md:text-8xl lg:text-[9rem] tracking-tight">
          <span className="block"><MaskedHeading text={t.hero.line1} onMount /></span>
          <span className="block text-brand text-glow italic"><MaskedHeading text={t.hero.line2} delay={0.15} onMount /></span>
          <span className="block"><MaskedHeading text={t.hero.line3} delay={0.3} onMount /></span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-8 max-w-xl font-body text-base md:text-lg text-white/70 leading-relaxed"
        >
          {t.hero.desc}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid="hero-reserve-btn"
            onClick={() => scrollTo("reserve")}
            className="bg-brand hover:bg-brand-light text-white font-body px-9 py-4 transition-colors hover:scale-[1.03] duration-300"
          >
            {t.hero.cta1}
          </button>
          <button
            data-testid="hero-events-btn"
            onClick={() => scrollTo("events")}
            className="border border-white/25 text-white hover:border-brand hover:text-brand font-body px-9 py-4 transition-colors"
          >
            {t.hero.cta2}
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollTo("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/50 hover:text-brand transition-colors"
      >
        <span className="font-display text-[10px] tracking-[0.3em] uppercase">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
          <ArrowDown size={18} />
        </motion.span>
      </motion.button>
    </section>
  );
};
